package com.karyadina.service

import com.karyadina.model.FlashCard_Subject
import com.karyadina.model.StudySession
import com.karyadina.model.StudySessionType
import com.karyadina.model.Unit as FlashcardUnit
import com.karyadina.util.currentTimeMillis

/**
 * Simple integration between Study Planner and Flashcards
 * This file handles all the automation between both tools
 */
class StudyFlashcardIntegration(
    private val flashcardService: FlashcardService,
    private val studyPlannerService: StudyPlannerService
) {
    
    /**
     * Get flashcard subjects that need review
     * Returns list of subjects with their due card counts
     */
    fun getFlashcardReviewNeeds(): List<FlashcardReviewNeed> {
        val subjects = flashcardService.getSubjects()
        val needsList = mutableListOf<FlashcardReviewNeed>()
        
        for (subject in subjects) {
            val stats = flashcardService.getSubjectStats(subject.id)
            if (stats.dueForReview > 0) {
                needsList.add(
                    FlashcardReviewNeed(
                        subjectId = subject.id,
                        subjectName = subject.name,
                        dueCards = stats.dueForReview,
                        totalCards = stats.totalCards,
                        estimatedMinutes = calculateReviewTime(stats.dueForReview),
                        Units = flashcardService.getUnits(subject.id)
                    )
                )
            }
        }
        
        return needsList.sortedByDescending { it.dueCards }
    }
    
    /**
     * Automatically create study sessions for flashcard reviews
     * Creates one session per day for the next 'days' days
     */
    fun autoScheduleFlashcardReviews(
        subjectId: String,
        days: Int = 7,
        preferredHour: Int = 9
    ): List<StudySession> {
        val subject = flashcardService.getSubject(subjectId) ?: return emptyList()
        val units = flashcardService.getUnits(subjectId)
        val sessions = mutableListOf<StudySession>()
        
        val currentTime = currentTimeMillis()
        val oneDayMillis = 24 * 60 * 60 * 1000L
        
        for (day in 0 until days) {
            val dayStart = currentTime + (day * oneDayMillis)
            val sessionTime = dayStart + (preferredHour * 60 * 60 * 1000L)
            
            // Check if there are due cards
            val dueCards = flashcardService.getDueCards(subjectId)
            if (dueCards.isNotEmpty()) {
                val duration = calculateReviewTime(dueCards.size)
                
                val session = StudySession(
                    id = "fs_${currentTimeMillis()}_${(1000..9999).random()}",
                    subject = subject.name,
                    topic = "Flashcard Review",
                    sessionType = StudySessionType.FLASHCARD_REVIEW.name,
                    startTime = sessionTime,
                    endTime = sessionTime + (duration * 60 * 1000L),
                    durationMinutes = duration,
                    linkedFlashcardSubjectId = subjectId
                )
                
                studyPlannerService.createSession(session)
                sessions.add(session)
            }
        }
        
        return sessions
    }
    
    /**
     * Create a quick study session for flashcard review
     */
    fun createQuickReviewSession(
        subjectId: String,
        unitId: String? = null,
        startTime: Long = currentTimeMillis()
    ): StudySession? {
        val subject = flashcardService.getSubject(subjectId) ?: return null
        
        val dueCards = if (unitId != null) {
            flashcardService.getDueCards(subjectId, unitId)
        } else {
            flashcardService.getDueCards(subjectId)
        }
        
        if (dueCards.isEmpty()) return null
        
        val duration = calculateReviewTime(dueCards.size)
        val unitName = unitId?.let { flashcardService.getUnit(it)?.name } ?: "All Units"
        
        val session = StudySession(
            id = "fs_${currentTimeMillis()}_${(1000..9999).random()}",
            subject = subject.name,
            topic = "Flashcard Review: $unitName",
            sessionType = StudySessionType.FLASHCARD_REVIEW.name,
            startTime = startTime,
            endTime = startTime + (duration * 60 * 1000L),
            durationMinutes = duration,
            linkedFlashcardSubjectId = subjectId,
            linkedFlashcardUnitId = unitId
        )
        
        studyPlannerService.createSession(session)
        return session
    }
    
    /**
     * Get study sessions that are linked to flashcards
     */
    fun getLinkedFlashcardSessions(): List<StudySession> {
        val allSessions = studyPlannerService.getAllSessions()
        return allSessions.filter { 
            it.sessionType == StudySessionType.FLASHCARD_REVIEW.name ||
            it.linkedFlashcardSubjectId != null 
        }
    }
    
    /**
     * Calculate estimated review time based on number of cards
     * Formula: 2 minutes per card, minimum 15 mins, maximum 60 mins
     */
    private fun calculateReviewTime(cardCount: Int): Int {
        val estimated = cardCount * 2
        return when {
            estimated < 15 -> 15
            estimated > 60 -> 60
            else -> estimated
        }
    }
}

/**
 * Data class for flashcard review needs
 */
data class FlashcardReviewNeed(
    val subjectId: String,
    val subjectName: String,
    val dueCards: Int,
    val totalCards: Int,
    val estimatedMinutes: Int,
    val Units: List<FlashcardUnit>
)
